import { useEffect, useState } from "react";
import type { PinNode } from "./act1.ts";
import { getReducedMotion } from "../../gamzone/settings.ts";
import { OptionsButton } from "./OptionsButton.tsx";
import { dName, dTitle } from "./loc.ts";
import { useGt } from "./gameText.ts";
import { useLang } from "../../gamzone/i18n.ts";

// Two emerald arts: the plain (thornless) stone for not-yet / in-progress,
// the thorn-caged stone for a sealed (cleared) node.
const PIN_PLAIN = "/assets/ui/pact-stone-pin-inactive.webp";
const PIN_SEALED = "/assets/ui/pact-stone-pin-active.webp";

export type PinState = "locked" | "active" | "cleared";

/**
 * ACT1_MAP_REVEAL / ACT1_MAP_READY — the world map with the three pact-stone
 * pins. On first reveal they fade in one by one. Three finishes from `stateOf`:
 *   locked  — plain emerald, black-and-white, no halo, not reachable yet
 *   active  — plain emerald, full colour + pulsing halo, the current node
 *   cleared — thorn-caged emerald, full colour, sealed, no halo
 * `active` and `cleared` are both clickable (a cleared pin replays its scene).
 */
export function ActMap({
  phase,
  actName,
  pins,
  mapBg,
  stateOf,
  prompt,
  onRevealDone,
  onPick,
  onPrevAct,
  onNextAct,
}: {
  phase: "mapReveal" | "mapReady";
  actName: string;
  pins: readonly PinNode[];
  mapBg: string;
  stateOf: (id: number) => PinState;
  prompt: string;
  onRevealDone: () => void;
  onPick: (id: number) => void;
  /** Browse to the previous/next unlocked 막's map without touching campaign
   *  progress — omit either to hide that arrow (start/end of what's unlocked
   *  so far). Lets a player who has moved on to a later 막 still get back to
   *  an earlier one instead of being stuck on whichever 막 is furthest. */
  onPrevAct?: () => void;
  onNextAct?: () => void;
}) {
  const lang = useLang();
  const gt = useGt();
  const reduced = getReducedMotion();
  // How many pins are visible so far. In mapReady all of them always are.
  const [shown, setShown] = useState(() =>
    phase === "mapReady" || reduced ? pins.length : 0,
  );

  useEffect(() => {
    if (phase !== "mapReveal") return undefined;
    if (reduced) {
      const t = setTimeout(onRevealDone, 400);
      return () => clearTimeout(t);
    }
    const timers: number[] = [];
    pins.forEach((_, n) => {
      timers.push(window.setTimeout(() => setShown(n + 1), 350 + n * 420));
    });
    timers.push(
      window.setTimeout(onRevealDone, 350 + pins.length * 420 + 550),
    );
    return () => timers.forEach(clearTimeout);
  }, [phase, reduced, onRevealDone, pins]);

  const ready = phase === "mapReady";

  return (
    <div
      className="cam-screen cam-map"
      style={{ ["--cam-bg" as string]: `url(${mapBg})` }}
    >
      <OptionsButton />

      {/* swaps the R and G channels — the emerald's green turns red while the
         near-neutral metal bezel and white sparkle are unchanged. Used by
         `.cam-pin.active img` (the seal that's breaking). */}
      <svg width="0" height="0" aria-hidden style={{ position: "absolute" }}>
        <filter id="cam-red-shift" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 1 0 0 0  1 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
          />
        </filter>
      </svg>

      {ready && onPrevAct && (
        <button
          type="button"
          className="cam-map-nav prev"
          aria-label={gt("prevAct")}
          onClick={onPrevAct}
        >
          ‹
        </button>
      )}
      <div className="cam-map-title">{actName}</div>
      {ready && onNextAct && (
        <button
          type="button"
          className="cam-map-nav next"
          aria-label={gt("nextAct")}
          onClick={onNextAct}
        >
          ›
        </button>
      )}

      <div className="cam-pin-layer">
        {pins.map((pin, n) => {
          // Once ready, every pin is just there — no re-reveal on 막
          // browsing (`shown` only staggers the very first reveal below).
          const visible = ready || n < shown;
          const st = ready ? stateOf(pin.id) : "locked";
          const style = {
            left: `${pin.pos.x * 100}%`,
            top: `${pin.pos.y * 100}%`,
            ["--pin-scale" as string]: pin.scale,
          };
          const cls = `cam-pin ${st}` + (visible ? " in" : "");
          const pinImg = st === "cleared" ? PIN_SEALED : PIN_PLAIN;

          if (st === "locked") {
            return (
              <div key={pin.id} className={cls} style={style} aria-hidden>
                <img src={pinImg} alt="" />
              </div>
            );
          }
          return (
            <button
              key={pin.id}
              type="button"
              className={cls}
              style={style}
              onClick={() => onPick(pin.id)}
            >
              {st === "active" && <span className="cam-pin-halo" aria-hidden />}
              <img src={pinImg} alt="" aria-hidden />
              <span className="cam-pin-label">
                {dName(pin.name, lang)}
                <em>{dTitle(pin.title, lang)}</em>
              </span>
            </button>
          );
        })}
      </div>

      {ready && <div className="cam-map-prompt">{prompt}</div>}
    </div>
  );
}
